import { useEffect, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { getCurrentThemeColor, normalizeTheme } from '../utils/listColorMapping';

/**
 * Custom hook that automatically updates list colors when theme changes
 * This ensures that when users switch between light/dark mode,
 * the list colors automatically map to their theme-appropriate counterparts
 */
export const useThemeAwareColors = (board, setBoard) => {
    const { theme } = useTheme();
    const [actualTheme, setActualTheme] = useState(() => normalizeTheme(theme));

    // Listen for data-theme attribute changes (which ThemeContext manages)
    useEffect(() => {
        const updateActualTheme = () => {
            const newActualTheme = normalizeTheme(theme);
            setActualTheme(newActualTheme);
        };

        // Update immediately
        updateActualTheme();

        // Listen for data-theme attribute changes on document element
        // This will catch both manual theme changes and system theme changes
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
                    updateActualTheme();
                }
            });
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme']
        });

        return () => observer.disconnect();
    }, [theme]);

    useEffect(() => {
        if (!board?.actionLists || board.actionLists.length === 0) {
            return;
        }

        // Check if any lists have colors that need theme mapping
        const listsWithColors = board.actionLists.filter(list => list.color);

        if (listsWithColors.length === 0) {
            return;
        }

        // Update all list colors to match the current theme
        setBoard(prevBoard => {
            if (!prevBoard?.actionLists) return prevBoard;

            const updatedLists = prevBoard.actionLists.map(list => {
                if (!list.color) return list;

                // Get the theme-appropriate color for this list using the actual resolved theme
                const themeColors = getCurrentThemeColor(list.color, actualTheme);

                // Only update if the colors actually changed
                if (themeColors.bg !== list.color || themeColors.text !== list.textColor) {
                    return {
                        ...list,
                        color: themeColors.bg,
                        textColor: themeColors.text
                    };
                }

                return list;
            });

            return {
                ...prevBoard,
                actionLists: updatedLists
            };
        });
    }, [actualTheme, board?.actionLists?.length, setBoard]);
};

export default useThemeAwareColors;
